import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, OnDestroy, OnInit, Output, ViewChild, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { EMPTY, Observable, Subject } from 'rxjs';
import { catchError, finalize, takeUntil } from 'rxjs/operators';
import { MyAuthService } from 'src/app/core/services/auth.service';
import { RouterModule } from '@angular/router';
import { MatDividerModule } from '@angular/material/divider';
import { ApiService, BillOfLadingLookupData, VesselSearchCandidate } from 'src/app/core/services/api.service';
import { TutorialAnchorDirective } from 'src/app/core/tutorial/tutorial-anchor.directive';
import { MobileNavigationDrawerComponent } from '../mobile-navigation-drawer/mobile-navigation-drawer.component';

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, MatButtonModule, MatMenuModule, MatIconModule, RouterModule, MatDividerModule, TutorialAnchorDirective, MobileNavigationDrawerComponent]
})
export class NavbarComponent implements OnInit, OnDestroy {
  auth = inject(AuthService);
  route = inject(Router);
  myAuthService = inject(MyAuthService)
  private apiService = inject(ApiService);
  private changeDetector = inject(ChangeDetectorRef);
  private destroy$ = new Subject<void>();

  isAdmin$: Observable<boolean> | undefined;
  isSales$: Observable<boolean> | undefined;
  searchQuery = '';
  searchMode: 'vessel' | 'bol' = 'vessel';
  vesselResults: VesselSearchCandidate[] = [];
  vesselSearchLoading = false;
  bolLookupLoading = false;
  vesselSearchError = '';
  vesselSearchSubmitted = false;
  vesselSearchActiveIndex = -1;
  productRequestAvailable = false;
  mobileDrawerOpen = false;
  @ViewChild('vesselSearchInput') private vesselSearchInput?: ElementRef<HTMLInputElement>;

  @Output() vesselSelected = new EventEmitter<VesselSearchCandidate>();
  @Output() productRequestRequested = new EventEmitter<string>();
  @Output() vesselSearchNoMatch = new EventEmitter<{ query: string; imo: string | null }>();
  @Output() bolLookupCompleted = new EventEmitter<BillOfLadingLookupData>();
  @Output() bolLookupCleared = new EventEmitter<void>();

  get isSearchLoading(): boolean { return this.vesselSearchLoading || this.bolLookupLoading; }
  get searchError(): string { return this.vesselSearchError; }
  get searchPlaceholder(): string {
    return this.searchMode === 'bol' ? 'Search Bill of Lading number' : 'Search vessel name or 7-digit IMO';
  }
  get searchButtonLabel(): string { return this.searchMode === 'bol' ? 'LOOK UP' : 'RESOLVE'; }

  focusSearch(): void { this.vesselSearchInput?.nativeElement.focus(); }

  ngOnInit(): void {
    this.isAdmin$ = this.myAuthService.isAdmin()
    this.isSales$ = this.myAuthService.isSales()
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onVesselSearch(): void {
    const query = this.searchQuery.trim();
    this.vesselSearchSubmitted = true;
    this.vesselSearchError = '';
    this.vesselResults = [];
    this.vesselSearchActiveIndex = -1;
    this.productRequestAvailable = false;

    if (!query || (!/^\d{7}$/.test(query) && query.length < 3)) {
      this.vesselSearchError = 'Enter a vessel name or a 7-digit IMO.';
      this.changeDetector.markForCheck();
      return;
    }

    this.vesselSearchLoading = true;
    this.apiService.searchVessels(query).pipe(
      takeUntil(this.destroy$),
      catchError(error => {
        this.vesselSearchError = [429, 502, 503].includes(error?.status) ? 'Vessel search is temporarily unavailable. Try again.' : error?.status === 401 ? 'Your session has expired. Sign in again.' : 'Vessel search is temporarily unavailable.';
        this.changeDetector.markForCheck();
        return EMPTY;
      }),
      finalize(() => {
        this.vesselSearchLoading = false;
        this.changeDetector.markForCheck();
      })
    ).subscribe(response => {
      this.vesselResults = (response.data || []).filter(item => Boolean(item && (item.imo || item.name)));
      if (!this.vesselResults.length) {
        this.vesselSearchError = 'No vessels found for this query.';
        this.productRequestAvailable = true;
        this.vesselSearchNoMatch.emit({ query, imo: /^\d{7}$/.test(query) ? query : null });
      }
      this.changeDetector.markForCheck();
    });
  }

  onVesselSearchKeydown(event: KeyboardEvent): void {
    if (!this.vesselSearchSubmitted || !this.vesselResults.length || this.searchMode !== 'vessel') return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const direction = event.key === 'ArrowDown' ? 1 : -1;
      this.vesselSearchActiveIndex = (this.vesselSearchActiveIndex + direction + this.vesselResults.length) % this.vesselResults.length;
      this.changeDetector.markForCheck();
      return;
    }
    if (event.key === 'Enter' && this.vesselSearchActiveIndex >= 0) {
      event.preventDefault();
      this.selectVesselResult(this.vesselResults[this.vesselSearchActiveIndex]);
    }
  }

  onSearch(): void {
    if (this.searchMode === 'bol') {
      this.onBillOfLadingLookup();
      return;
    }
    this.onVesselSearch();
  }

  setSearchMode(mode: 'vessel' | 'bol'): void {
    if (this.searchMode === mode) return;
    this.searchMode = mode;
    this.clearVesselSearch();
    if (mode === 'vessel') this.bolLookupCleared.emit();
    this.changeDetector.markForCheck();
  }

  private onBillOfLadingLookup(): void {
    const billOfLadingNumber = this.searchQuery.trim().toUpperCase();
    this.vesselSearchSubmitted = true;
    this.vesselSearchError = '';
    this.vesselResults = [];
    this.productRequestAvailable = false;

    if (!/^[A-Z0-9._/-]{3,100}$/.test(billOfLadingNumber)) {
      this.vesselSearchError = 'Enter a Bill of Lading number using letters, numbers, periods, underscores, slashes, or hyphens.';
      this.changeDetector.markForCheck();
      return;
    }

    this.searchQuery = billOfLadingNumber;
    this.bolLookupLoading = true;
    this.apiService.lookupBillOfLading(billOfLadingNumber).pipe(
      takeUntil(this.destroy$),
      catchError(error => {
        this.vesselSearchError = error?.status === 401
          ? 'Your session has expired. Sign in again.'
          : error?.status === 429
            ? 'Bill of Lading lookup is temporarily rate limited. Try again shortly.'
            : [502, 503].includes(error?.status)
              ? 'Bill of Lading lookup is temporarily unavailable. Try again.'
              : error?.status === 400
                ? 'The Bill of Lading number is invalid.'
                : 'Bill of Lading lookup is temporarily unavailable.';
        this.changeDetector.markForCheck();
        return EMPTY;
      }),
      finalize(() => {
        this.bolLookupLoading = false;
        this.changeDetector.markForCheck();
      })
    ).subscribe(response => {
      this.vesselSearchSubmitted = false;
      this.bolLookupCompleted.emit(response.data);
      this.changeDetector.markForCheck();
    });
  }

  clearVesselSearch(): void {
    this.searchQuery = '';
    this.vesselResults = [];
    this.vesselSearchError = '';
    this.vesselSearchSubmitted = false;
    this.productRequestAvailable = false;
    this.vesselSearchActiveIndex = -1;
  }

  selectVesselResult(vessel: VesselSearchCandidate): void {
    this.vesselSelected.emit(vessel);
    this.vesselSearchSubmitted = false;
    this.vesselSearchActiveIndex = -1;
  }

  trackVesselResult(index: number, vessel: VesselSearchCandidate): string {
    return vessel.uuid || vessel.imo || vessel.mmsi || `${vessel.name || 'vessel'}-${vessel.type || 'unknown'}-${vessel.countryIso || 'unknown'}-${index}`;
  }

  requestProduct(): void {
    this.productRequestRequested.emit(this.searchQuery.trim());
  }

  navigateToMyAccount() {
    this.route.navigate(['account']);
  }

  navigateToAdmin() {
    this.route.navigate(['admin']);
  }

  navigateToHome() {
    this.route.navigate(['']);
  }

  toggleMobileNavigation(): void { this.mobileDrawerOpen = !this.mobileDrawerOpen; }
  closeMobileNavigation(): void { this.mobileDrawerOpen = false; }
}
